import React, { useMemo } from "react";
import { CheckOutlined, CloseOutlined } from "@ant-design/icons";

const DEFAULT_LABELS = {
	team_leader: "팀",
	office_director: "국",
	church_leader: "당",
	팀장: "팀",
	국장: "국",
	당회장: "당",
};

function shortLabel(step, index) {
	if (!step) return ["팀", "국", "당"][index] || `${index + 1}`;
	const raw =
		step.short_label ||
		step.label ||
		step.role ||
		step.role_key ||
		step.name ||
		"";
	return DEFAULT_LABELS[raw] || String(raw).slice(0, 2) || ["팀", "국", "당"][index];
}

function normalizeStatus(status) {
	const s = String(status || "").toLowerCase();
	if (s === "approved" || s === "승인" || s === "not_required") {
		return s === "not_required" ? "skipped" : "approved";
	}
	if (s === "rejected" || s === "반려") return "rejected";
	if (s === "pending" || s === "waiting" || s === "대기" || !s) {
		return "pending";
	}
	return "pending";
}

/** 결재 라우트(approval_steps) 없이 남은 예전 신청 — 실제 결재 진행이 없었다면 UI·집계에서 제외 */
export function isLegacyWithoutApprovalWorkflow(record) {
	if (!record || record.is_cancelled) {
		return false;
	}
	const steps = record.approval_steps;
	if (Array.isArray(steps) && steps.length > 0) {
		return false;
	}
	const statuses = [
		record.team_leader_status,
		record.office_director_status,
		record.church_leader_status,
	];
	const hasDecided =
		record.team_leader_decided_at ||
		record.office_director_decided_at ||
		record.church_leader_decided_at;
	const hasApprover =
		record.team_leader_approver ||
		record.office_director_approver ||
		record.church_leader_approver;
	if (hasDecided || hasApprover) {
		return false;
	}
	if (
		statuses.some(
			(s) =>
				s === "approved" ||
				s === "rejected" ||
				s === "승인" ||
				s === "반려",
		)
	) {
		return false;
	}
	if (statuses.every((s) => !s || s === "not_required")) {
		return true;
	}
	return statuses.some(
		(s) => s === "pending" || s === "waiting" || s === "대기",
	);
}

/**
 * Build a compact approval progress UI (팀/국/당 circles + caption).
 */
export function deriveApprovalSteps(record) {
	if (!record) return [];
	if (isLegacyWithoutApprovalWorkflow(record)) {
		return [];
	}
	if (Array.isArray(record.approval_steps) && record.approval_steps.length) {
		return [...record.approval_steps]
			.sort((a, b) => (a.order ?? a.step_order ?? 0) - (b.order ?? b.step_order ?? 0))
			.map((step, index) => ({
				key: step.key || step.role_key || `step-${index}`,
				label: shortLabel(step, index),
				status: normalizeStatus(step.status),
				fullLabel: step.label || step.role || shortLabel(step, index),
			}));
	}
	const legacy = [
		{ key: "team_leader", label: "팀", status: record.team_leader_status },
		{ key: "office_director", label: "국", status: record.office_director_status },
		{ key: "church_leader", label: "당", status: record.church_leader_status },
	];
	if (
		legacy.every(
			(s) => s.status === undefined || s.status === null || s.status === "",
		)
	) {
		return [];
	}
	return legacy.map((s) => ({
		...s,
		status: normalizeStatus(s.status),
		fullLabel: s.label,
	}));
}

export function deriveApprovalState(record) {
	if (!record) return "pending";
	if (record.is_cancelled) return "cancelled";
	if (isLegacyWithoutApprovalWorkflow(record)) {
		return record.is_applied ? "done" : "legacy";
	}
	const steps = deriveApprovalSteps(record);
	if (!steps.length) {
		if (record.is_applied) return "done";
		return "pending";
	}
	if (steps.some((s) => s.status === "rejected")) return "rejected";
	const actionable = steps.filter((s) => s.status !== "skipped");
	if (!actionable.length) {
		return record.is_applied ? "done" : "legacy";
	}
	if (actionable.every((s) => s.status === "approved")) {
		return "done";
	}
	if (actionable.every((s) => s.status === "pending")) return "pending";
	return "progress";
}

export function approvalStateLabel(state) {
	switch (state) {
		case "cancelled":
			return "취소됨";
		case "rejected":
			return "반려";
		case "done":
			return "승인 완료";
		case "progress":
			return "결재 진행중";
		case "legacy":
			return "결재 없음";
		case "pending":
		default:
			return "결재 대기";
	}
}

function daysSince(dateStr) {
	if (!dateStr) return null;
	const d = new Date(String(dateStr).slice(0, 10));
	if (Number.isNaN(d.getTime())) return null;
	const today = new Date();
	today.setHours(0, 0, 0, 0);
	d.setHours(0, 0, 0, 0);
	return Math.max(0, Math.round((today - d) / 86400000));
}

function buildCaption(steps, record, state) {
	if (state === "cancelled") return "취소됨";
	if (state === "rejected") {
		const rejected = steps.find((s) => s.status === "rejected");
		return rejected ? `${rejected.fullLabel} 반려` : "반려";
	}
	if (state === "done") return "결재 완료";
	const pending = steps.find((s) => s.status === "pending");
	const days = daysSince(record?.application_date || record?.created_at);
	if (pending) {
		if (days === 0) return `${pending.fullLabel} 결재 대기 · 오늘 신청`;
		if (days != null) return `${pending.fullLabel} 결재 대기 · ${days}일째`;
		return `${pending.fullLabel} 결재 대기`;
	}
	return "결재 진행중";
}

const ApprovalSteps = ({ record, className = "" }) => {
	const steps = useMemo(() => deriveApprovalSteps(record), [record]);
	const state = useMemo(() => deriveApprovalState(record), [record]);
	const caption = useMemo(
		() => buildCaption(steps, record, state),
		[steps, record, state],
	);

	if (!steps.length) {
		return <span className="approval-steps__empty">—</span>;
	}

	return (
		<div className={`approval-steps ${className}`.trim()}>
			<div className="approval-steps__track" aria-hidden="true">
				{steps.map((step, index) => (
					<React.Fragment key={step.key}>
						{index > 0 ? (
							<span
								className={`approval-steps__line approval-steps__line--${
									steps[index - 1].status === "approved" ||
									steps[index - 1].status === "skipped"
										? "done"
										: "idle"
								}`}
							/>
						) : null}
						<span
							className={`approval-steps__node approval-steps__node--${step.status}`}
							title={step.fullLabel}>
							{step.status === "approved" || step.status === "skipped" ? (
								<CheckOutlined />
							) : step.status === "rejected" ? (
								<CloseOutlined />
							) : (
								<span className="approval-steps__label">{step.label}</span>
							)}
						</span>
					</React.Fragment>
				))}
			</div>
			<div
				className={`approval-steps__caption approval-steps__caption--${state}`}>
				{caption}
			</div>
		</div>
	);
};

export default ApprovalSteps;
